import React, { useEffect, useState } from 'react'
import { HiArrowsPointingIn, HiArrowsPointingOut, HiCheckCircle, HiXMark } from 'react-icons/hi2'
import { Button } from '../ui/button'
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog'
import { cn } from '../../lib/utils'
import { MAX_PAGE_WIDTH, type PdfPages } from './usePdfPages'

interface DocumentReaderProps {
  open: boolean
  onClose: () => void
  /** What is being read ("policy", "memo"), used in the reader's wording. */
  noun: string
  icon: React.ReactNode
  eyebrow: React.ReactNode
  title?: string
  pages: PdfPages
  pageCount?: number
  isLoading: boolean
  failed: boolean
  /** Shown instead of the drawn PDF pages. */
  content?: React.ReactNode
  /** The viewer has something to confirm. Otherwise `message` says why not. */
  canConfirm: boolean
  /** The viewer has already confirmed it; `message` is shown as a success. */
  done?: boolean
  message?: string
  onConfirm: () => Promise<void>
}

/**
 * The shared read-and-confirm dialog: a sticky header, the document at full width,
 * and a footer with any status note and the one acknowledgement button,
 * "I have read and understood this …", which unlocks once the reader reaches the end.
 */
export const DocumentReader: React.FC<DocumentReaderProps> = ({
  open,
  onClose,
  noun,
  icon,
  eyebrow,
  title,
  pages,
  pageCount,
  isLoading,
  failed,
  content,
  canConfirm,
  done = false,
  message = '',
  onConfirm,
}) => {
  const { scrollRef, canvasContainerRef, onScroll, progress, reachedEnd } = pages
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)
  // Why Confirm isn't ready yet, shown when someone presses it too early.
  const [nudge, setNudge] = useState<string | null>(null)

  const guidance = canConfirm
    ? reachedEnd
      ? 'You’ve reached the end.'
      : `Scroll to the end of the ${noun} to confirm you’ve read it.`
    : message
  const notReady = !reachedEnd
  // The footer only speaks up when it has something to say.
  const footerText = nudge ?? (canConfirm ? '' : guidance)
  const confirmText = `I have read and understood this ${noun}`

  useEffect(() => {
    if (reachedEnd) setNudge(null)
  }, [reachedEnd])

  const explainNotReady = () =>
    setNudge(`Read the whole ${noun} first. Scroll down to the end, then confirm.`)

  const handleConfirm = async () => {
    if (isSubmitting) return
    if (notReady) {
      explainNotReady()
      return
    }
    setIsSubmitting(true)
    try {
      await onConfirm()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        // Start on the document so Space and Page Down scroll it straight away.
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          scrollRef.current?.focus({ preventScroll: true })
        }}
        className={cn(
          'flex flex-col gap-0 overflow-hidden border-0 bg-white p-0 shadow-2xl',
          fullscreen
            ? 'h-[100dvh] w-screen max-w-none rounded-none'
            : 'h-[calc(100dvh-1.5rem)] w-[calc(100vw-1.5rem)] max-w-6xl rounded-2xl'
        )}
      >
        <header className="flex shrink-0 items-center gap-3 border-b border-slate-200 px-4 py-3 sm:px-5">
          <span className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 sm:flex">
            {icon}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs font-medium text-slate-500">
              {eyebrow}
            </div>
            <DialogTitle className="truncate text-base font-semibold text-slate-900 sm:text-lg">
              {title || `Loading ${noun}…`}
            </DialogTitle>
            <DialogDescription className="sr-only">{guidance}</DialogDescription>
          </div>
          {/* How much there is and how far you've read, beside the full screen button. */}
          <span className="shrink-0 text-xs font-medium tabular-nums text-slate-500">
            <span className="hidden sm:inline">
              {pageCount ? `${pageCount} ${pageCount === 1 ? 'page' : 'pages'} · ` : ''}
            </span>
            {progress}% read
          </span>
          <button
            type="button"
            onClick={() => setFullscreen((v) => !v)}
            className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-lg p-0 text-slate-500 hover:bg-slate-100 hover:text-slate-800 sm:flex"
            aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
            title={fullscreen ? 'Exit full screen' : 'Full screen'}
          >
            {fullscreen ? (
              <HiArrowsPointingIn className="h-5 w-5" />
            ) : (
              <HiArrowsPointingOut className="h-5 w-5" />
            )}
          </button>
          <DialogClose
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg p-0 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            aria-label="Close"
          >
            <HiXMark className="h-6 w-6" />
          </DialogClose>
        </header>

        <div
          className="h-1 shrink-0 bg-slate-100"
          role="progressbar"
          aria-label="Reading progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress}
        >
          <div
            className="h-full bg-blue-500 transition-[width] duration-200"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="flex min-h-0 flex-1">
          {/* Document, full width (container always mounted so pdf.js has a target) */}
          <div
            ref={scrollRef}
            onScroll={onScroll}
            tabIndex={0}
            aria-label={`${noun[0].toUpperCase()}${noun.slice(1)} document`}
            className="min-w-0 flex-1 overflow-y-auto bg-slate-100 px-3 py-4 outline-none sm:px-8 sm:py-6"
          >
            <div className="mx-auto" style={{ maxWidth: MAX_PAGE_WIDTH }}>
              {isLoading && (
                <div className="space-y-4" role="status" aria-label={`Loading ${noun}`}>
                  {[0, 1].map((key) => (
                    <div
                      key={key}
                      className="aspect-[1/1.3] animate-pulse rounded-lg bg-white/80 ring-1 ring-slate-200"
                    />
                  ))}
                </div>
              )}
              {failed && !isLoading && (
                <p className="py-16 text-center text-sm text-slate-500">
                  Couldn’t load this {noun}. Close it and try again.
                </p>
              )}
              {content ?? <div ref={canvasContainerRef} className="space-y-4" />}
              {!isLoading && !failed && (
                <p className="py-6 text-center text-xs font-medium text-slate-400">End of {noun}</p>
              )}
            </div>
          </div>
        </div>

        {/* The acknowledgement sits in the footer; the header ✕ closes. */}
        <footer className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-t border-slate-200 bg-white px-4 py-3 sm:flex-nowrap sm:px-5">
          <p
            role="status"
            className={cn(
              'min-w-0 basis-full items-center gap-2 text-sm sm:flex sm:flex-1 sm:basis-auto',
              footerText ? 'flex' : 'hidden',
              nudge ? 'font-medium text-amber-700' : done ? 'text-emerald-700' : 'text-slate-500'
            )}
          >
            {done && !nudge && <HiCheckCircle className="h-5 w-5 shrink-0" aria-hidden="true" />}
            <span className="sm:truncate">{footerText}</span>
          </p>
          {canConfirm && (
            <Button
              variant="primary"
              size="sm"
              className={cn(
                'h-10 min-w-0 flex-1 px-4 sm:flex-none',
                notReady && 'cursor-not-allowed opacity-50'
              )}
              aria-label={confirmText}
              aria-disabled={notReady || isSubmitting}
              disabled={isSubmitting}
              onClick={handleConfirm}
            >
              <span className="truncate">
                {isSubmitting ? (
                  'Saving…'
                ) : (
                  <>
                    <span className="hidden sm:inline">{confirmText}</span>
                    <span className="sm:hidden">I’ve read and understood it</span>
                  </>
                )}
              </span>
            </Button>
          )}
        </footer>
      </DialogContent>
    </Dialog>
  )
}
